<script setup>
import Facebook from 'assets/logos/facebook-logo.svg'
import Instagram from 'assets/icons/instagram.svg'
import TikTok from 'assets/icons/tiktok.svg'

const { t } = useI18n()

const currentYear = computed(() => {
    return new Date().getFullYear()
})

const route = useRoute()

/**
 *  Show Cookiebot
 */
const showCB = () => {
    Cookiebot.show()
}

const footerMenu = computed(() => {
    return [
        {
            label: t('footer.imprint'),
            external: true,
            url: 'https://www.initiative-vermisste-kinder.de/about/'
        },
        {
            label: t('footer.data_protection'),
            url: 'dataProtection'
        },
        {
            label: t('footer.legal'),
        }
    ]
})

const footerContact = computed(() => {
    return `${t('footer.contact')}: <a href="tel:+40609454340">(040) 609454340</a><br> Telefax: <a href="tel:+40609454349">(040) 609454349</a><br> E-Mail: <a href="mailto:info@vermisste-kinder.de">info@vermisste-kinder.de</a><br>`
})

const footerHotline = computed(() => {
    return `${t('footer.hotline')}:  <a href="tel:116000">116000</a> (24/7)`
})

const linkList = computed(() => {
    return [
        {
            ariaLabel: t('footer.infos.ariaLabel0'),
            url: 'https://www.facebook.com/deutschlandfindeteuch/',
            icon: Facebook,
            tracking: [
                { track: 'click' },
                { 'track-position': 2 },
                { 'track-name': t('footer.infos.ariaLabel0') },
                { 'track-url': route.fullPath }
            ]
        },
        {
            ariaLabel: t('footer.infos.ariaLabel1'),
            url: 'https://www.instagram.com/vermisste_kinder_de/',
            icon: Instagram,
            tracking: [
                { track: 'click' },
                { 'track-position': 2 },
                { 'track-name': t('footer.infos.ariaLabel1') },
                { 'track-url': route.fullPath }
            ]
        },
        {
            ariaLabel: t('footer.infos.ariaLabel2'),
            url: 'https://www.tiktok.com/@vermis_st',
            icon: TikTok,
            tracking: [
                { track: 'click' },
                { 'track-position': 2 },
                { 'track-name': t('footer.infos.ariaLabel2') },
                { 'track-url': route.fullPath }
            ]
        }
    ]
})

const dataAttributes = (tracking) => {
    if (!tracking) return {}

    const attributes = {}
    tracking.forEach((obj) => {
        for (const [key, value] of Object.entries(obj)) {
            attributes[`data-${key}`] = value
        }
    })
    return attributes
}
</script>

<template>
    <footer class="page-footer">
        <div class="page-footer-inner">
            <nav class="page-footer-legal">
                <address class="page-footer-address" v-html="t('footer.address')" />

                <p v-html="footerContact" />

                <p v-html="footerHotline" />

                <ul class="page-footer-link-list">
                    <li v-for="({ icon, url, ariaLabel, tracking }, index) in linkList" :key="index + ariaLabel" class="page-footer-link-list-item">
                        <NuxtLink
                            :to="url" rel="noreferrer nofollow" target="_blank" v-bind="dataAttributes(tracking)"
                            :aria-label="ariaLabel"
                        >
                            <component :is="icon" />
                        </NuxtLink>
                    </li>
                </ul>

                <ul class="page-footer-legal-menu">
                    <li v-for="(link, index) in footerMenu" :key="`footer-link-${index}`">
                        <NuxtLink v-if="link.url && link.external" :to="link.url" :target="link.external? '_blank' : ''" :rel="link.external? 'noopener noreferrer' : ''">
                            {{ link.label }}
                        </NuxtLink>
                        <NuxtLinkLocale v-else-if="link.url" :to="{ name: link.url }">
                            {{ link.label }}
                        </NuxtLinkLocale>
                        <button v-else class="cookiebot-cta" @click="showCB">
                            {{ link.label }}
                        </button>
                    </li>
                </ul>

                <p class="page-footer-copyright" v-html="t('footer.copyRight')?.replace('%s', currentYear)" />
            </nav>
        </div>
    </footer>
</template>

<style lang="scss">
.page-footer {
    border-top: $page-footer-border 2px solid;
    background: $page-footer-bg;
    position: relative;
    z-index: z('page-footer');

    @include fullbleed;

    .cookiebot-cta,
    a {
        color: inherit;
        text-decoration: none;
        cursor: pointer;
        appearance: none;
        border: 0 none;
        background: none;
        font-size: inherit;
        font-family: inherit;

            &:hover {
                text-decoration: underline;
            }
    }
}

.page-footer-inner {
    grid-column: 2 / -2;
    padding-block: $page-grid-gap-mobile * 2;

    @include section-padding;
}

.page-footer-legal {
    grid-column: 1 / -1;
}

.page-footer-address {
    font-weight: 800;
    font-family: $font-headline;
    font-style: normal;
}

.page-footer-legal-menu {
    display: flex;
    gap: px2rem(10);
    justify-content: flex-start;
    align-items: center;
    list-style: none;
    padding: 0;
    margin: px2rem(60) 0 0;

    li {
        color: $page-footer-legal;
        font-size: px2rem(14);

        &:last-child {
            &::after {
                display: none;
            }
        }

        &::after {
            display: inline-block;
            content: '/';
            padding-inline-start: px2rem(10);
        }
    }
}

.page-footer-copyright {
    color: $page-footer-legal;
    font-size: px2rem(14);
    margin: px2rem(10) 0 0;
}

.page-footer-link-list {
    margin-block: px2rem(30) px2rem(10);
    list-style-type: none;
    padding: 0;
    display: flex;
}

.page-footer-link-list-item {
    border-right: $confirmation-infos-link-list-item-border 1px solid;
    cursor: pointer;

    &:last-child {
        border-right: none;
    }

    &:first-child {
        a {
            padding-inline-start: 0;
        }
    }

    a {
        display: block;
        padding-inline: 20px;
    }

    svg {
        fill: $body-text;
        height: 30px;
        display: block;
        width: auto;
    }
}
</style>
